"use client";

import { useEffect, useMemo, useState, type ElementType } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown, ChevronRight, Menu, X } from "lucide-react";
import { navData } from "@/data/navData";
import { trackEvent, trackPhoneClick } from "@/utils/gtm";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import { whatsappMessages } from "@/utils/whatsapp";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

const BADGE_STYLES: Record<string, string> = {
  Hot: "bg-rose-100 text-rose-700 border border-rose-200",
  New: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  Popular: "bg-sky-100 text-sky-700 border border-sky-200",
};

interface CategoryIconProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: ElementType | any;
  className?: string;
}

function CategoryIcon({ icon, className = "" }: CategoryIconProps) {
  if (!icon) return null;

  if (typeof icon === "string") {
    return <span className={className}>{icon}</span>;
  }

  const Icon = icon;
  return <Icon className={className} aria-hidden="true" strokeWidth={2.25} />;
}

export default function MegaMenu() {
  const [desktopOpenMenu, setDesktopOpenMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState<string | null>(null);
  const [mobileOpenCourseCategory, setMobileOpenCourseCategory] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const coursesMenu = useMemo<any>(
    () => navData.mainLinks.find((link) => link.label === "Courses"),
    []
  );

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
        setMobileOpenSection(null);
        setMobileOpenCourseCategory(null);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileSection = (label: string) => {
    setMobileOpenSection((prev) => (prev === label ? null : label));
  };

  const toggleMobileCourseCategory = (title: string) => {
    setMobileOpenCourseCategory((prev) => (prev === title ? null : title));
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-100">
      <div
        className="relative"
        onMouseLeave={() => setDesktopOpenMenu(null)}
      >
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-20 flex items-center justify-between gap-4">
          <Link href={navData.logo.href} className="flex items-center shrink-0" aria-label="Seasoft Classes home">
            <Image
              src="/images/seasoft-logo.svg"
              alt="Seasoft Classes"
              width={200}
              height={58}
              priority
              className="h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-10 h-full">
            {navData.mainLinks.map((link: any) => {
              const hasMegaMenu = Boolean(link.megaMenu);
              const hasDropdown = Boolean(link.dropdown);
              const isOpen = desktopOpenMenu === link.label;

              if (hasMegaMenu || hasDropdown) {
                return (
                  <div
                    key={link.label}
                    className="relative h-full flex items-center"
                    onMouseEnter={() => setDesktopOpenMenu(link.label)}
                  >
                    <button
                      type="button"
                      className={`flex items-center gap-1 text-[15px] font-semibold transition-colors cursor-pointer ${
                        isOpen ? "text-[#2563EB]" : "text-[#666666] hover:text-[#2563EB]"
                      }`}
                      aria-expanded={isOpen ? "true" : "false"}
                      aria-haspopup="true"
                    >
                      {link.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    {hasDropdown && isOpen ? (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 rounded-xl border border-gray-100 bg-white shadow-xl p-2 z-30">
                        {link.dropdown.map((item: any) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            aria-label={item.label?.includes("/") ? "Learn more about us" : `Go to ${item.label}`}
                            className="block rounded-lg px-3 py-2.5 text-[14px] font-medium text-[#4B5563] hover:text-[#2563EB] hover:bg-[#F8FAFF] transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              }

              return (
                <Link
                  key={link.label}
                  href={link.href}
                  aria-label={link.label?.includes("/") ? "Learn more about us" : `Go to ${link.label}`}
                  className="text-[15px] font-semibold text-[#666666] hover:text-[#2563EB] transition-colors"
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden lg:flex items-center gap-3">
              <WhatsAppButton
                variant="solid"
                size="sm"
                label="WhatsApp Us"
                message={whatsappMessages.counseling}
                context="header_nav"
                className="!bg-[#075E54] !text-white hover:!bg-[#05403a] transition-colors"
              />

              <div className="h-7 w-px bg-gray-200" />

              <a
                href="tel:+17087669765"
                onClick={() => trackPhoneClick("+17087669765", "/")}
                className="text-[14px] font-semibold text-[#475569] hover:text-[#2563EB]"
              >
                +1 (708) 766-9765
              </a>
            </div>

            <button
              type="button"
              className="lg:hidden p-2 text-black"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu size={30} />
            </button>
          </div>
        </div>

        {desktopOpenMenu === "Courses" && coursesMenu ? (
          <div className="hidden lg:block absolute top-full inset-x-0 bg-[#f4f4f4] border-t border-gray-100 shadow-2xl transition-all duration-300 ease-in-out">
            <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
              <div className="grid grid-cols-12 gap-8">
                <div className="col-span-12 grid grid-cols-2 xl:grid-cols-5 gap-6">
                  {coursesMenu.categories.map((category: any) => (
                    <div key={category.title} className="space-y-4">
                      <div className="flex items-start gap-2">
                        <CategoryIcon icon={category.icon} className="h-4 w-4 shrink-0 text-[#2563EB]" />
                        <h3 className="text-[13px] font-black tracking-wide text-[#111827] uppercase">
                          {category.title}
                        </h3>
                      </div>

                      <ul className="space-y-2.5">
                        {category.courses.map((course: any) => (
                          <li key={course.href}>
                            <Link
                              href={course.href}
                              onClick={() => {
                                trackEvent("Navigation", "mega_menu_click", course.label);
                                setDesktopOpenMenu(null);
                              }}
                              className="inline-flex items-center gap-2 text-[14px] text-[#4B5563] hover:text-[#2563EB] transition-colors"
                            >
                              <span>{course.label}</span>
                              {course.badge ? (
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    BADGE_STYLES[course.badge] || "bg-gray-100 text-gray-700 border border-gray-200"
                                  }`}
                                >
                                  {course.badge}
                                </span>
                              ) : null}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {isMobileMenuOpen ? (
        <>
          <button
            type="button"
            className="lg:hidden fixed inset-0 bg-black/50 z-[60]"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu backdrop"
          />

          <aside className="lg:hidden fixed top-0 right-0 z-[70] h-full w-[88%] max-w-[420px] bg-white shadow-2xl overflow-y-auto">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <Link href={navData.logo.href} onClick={() => setIsMobileMenuOpen(false)} aria-label="Seasoft Classes home">
                <Image
                  src="/images/seasoft-logo.svg"
                  alt="Seasoft Classes"
                  width={170}
                  height={50}
                  className="h-10 w-auto"
                  priority
                />
              </Link>

              <button
                type="button"
                className="p-2 text-gray-500"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X size={28} />
              </button>
            </div>

            <nav className="py-3">
              {navData.mainLinks.map((link: any) => {
                const hasMegaMenu = Boolean(link.megaMenu);
                const hasDropdown = Boolean(link.dropdown);
                const isSectionOpen = mobileOpenSection === link.label;

                if (!hasMegaMenu && !hasDropdown) {
                  return (
                    <Link
                      key={link.label}
                      href={link.href}
                      className="block px-6 py-4 text-[16px] font-semibold text-[#111827] border-b border-gray-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  );
                }

                return (
                  <div key={link.label} className="border-b border-gray-50">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection(link.label)}
                      className="w-full px-6 py-4 text-left text-[16px] font-semibold text-[#111827] flex items-center justify-between"
                    >
                      <span>{link.label}</span>
                      <ChevronDown
                        size={18}
                        className={`text-gray-400 transition-transform ${isSectionOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    {isSectionOpen ? (
                      <div className="bg-[#F9FAFB] px-4 pb-4">
                        {hasMegaMenu ? (
                          <div className="space-y-2 pt-2">
                            {link.categories.map((category: any) => {
                              const isCategoryOpen = mobileOpenCourseCategory === category.title;

                              return (
                                <div
                                  key={category.title}
                                  className="rounded-lg border border-gray-200 bg-white"
                                >
                                  <button
                                    type="button"
                                    onClick={() => toggleMobileCourseCategory(category.title)}
                                    className="w-full px-3 py-3 text-left flex items-center justify-between"
                                  >
                                    <span className="text-[14px] font-bold text-[#111827] inline-flex items-center gap-2">
                                      <CategoryIcon icon={category.icon} className="h-4 w-4 shrink-0 text-[#2563EB]" />
                                      <span>{category.title}</span>
                                    </span>
                                    <ChevronRight
                                      size={16}
                                      className={`text-gray-400 transition-transform ${isCategoryOpen ? "rotate-90" : ""}`}
                                    />
                                  </button>

                                  {isCategoryOpen ? (
                                    <ul className="px-3 pb-3 space-y-2 list-disc list-outside marker:text-[#2563EB] marker:text-[12px] pl-10">
                                      {category.courses.map((course: any) => (
                                        <li key={course.href}>
                                          <Link
                                            href={course.href}
                                            className="inline-flex items-center gap-2 text-[14px] text-[#4B5563] hover:text-[#2563EB]"
                                            onClick={() => {
                                              trackEvent("Navigation", "mega_menu_click", course.label);
                                              setIsMobileMenuOpen(false);
                                            }}
                                          >
                                            <span>{course.label}</span>
                                            {course.badge ? (
                                              <span
                                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                                  BADGE_STYLES[course.badge] || "bg-gray-100 text-gray-700 border border-gray-200"
                                                }`}
                                              >
                                                {course.badge}
                                              </span>
                                            ) : null}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <ul className="pt-2 space-y-1 list-disc list-outside marker:text-[#2563EB] marker:text-[12px] pl-10">
                            {link.dropdown.map((item: any) => (
                              <li key={item.href}>
                                <Link
                                  href={item.href}
                                  className="block py-2 pr-3 text-[14px] font-medium text-[#4B5563] hover:text-[#2563EB] hover:bg-white rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {item.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </nav>

            <div className="p-5 border-t border-gray-100">
              <WhatsAppButton
                variant="solid"
                size="md"
                label="Chat with Us on WhatsApp"
                message={whatsappMessages.default}
                context="mobile_menu"
                className="w-full justify-center mb-3"
              />

              <EnrollButton
                label="Enroll Now"
                variant="primary"
                size="sm"
                context={FORM_CONTEXTS.NAV_ENROLL}
                icon={false}
                className="w-full justify-center"
                onClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </aside>
        </>
      ) : null}
    </header>
  );
}

